import type {
  SurfaceProfileDto,
  UpsertSurfaceProfileDto,
} from '@terra/shared-contracts-external-network';
import { CompanySizeSchema } from '@terra/shared-schemas-orchestrator';

export type BusinessProfileEntity = SurfaceProfileDto['divisions'][number];

export type BusinessProfileCompanySize = NonNullable<
  SurfaceProfileDto['companySize']
>;

export type BusinessProfileEntityFormValues = {
  name: string;
  description: string;
  location: string;
};

export const BUSINESS_PROFILE_TEXT_FIELD = {
  DESCRIPTION: 'description',
  PRIMARY_INDUSTRY: 'primaryIndustry',
} as const;

export type BusinessProfileTextField =
  (typeof BUSINESS_PROFILE_TEXT_FIELD)[keyof typeof BUSINESS_PROFILE_TEXT_FIELD];

export const PROFILE_LIST_VALUE_ERROR = {
  REQUIRED: 'required',
  DUPLICATE: 'duplicate',
} as const;

export type ProfileListValueError =
  (typeof PROFILE_LIST_VALUE_ERROR)[keyof typeof PROFILE_LIST_VALUE_ERROR];

export type ProfileListDialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; index: number };

export const EMPTY_BUSINESS_PROFILE_ENTITY_FORM: BusinessProfileEntityFormValues =
  {
    name: '',
    description: '',
    location: '',
  };

export const BUSINESS_PROFILE_COMPANY_SIZES: readonly BusinessProfileCompanySize[] =
  CompanySizeSchema.options;

export const normalizeOptionalText = (value: string): string | null =>
  value.trim() || null;

const toComparableValue = (value: string): string => value.trim().toLowerCase();

export const getEditIndex = (state: ProfileListDialogState): number | null =>
  state.kind === 'edit' ? state.index : null;

export const validateProfileListValue = ({
  values,
  draft,
  editIndex,
}: {
  values: string[];
  draft: string;
  editIndex: number | null;
}): ProfileListValueError | null => {
  const candidate = toComparableValue(draft);

  if (!candidate) {
    return PROFILE_LIST_VALUE_ERROR.REQUIRED;
  }

  const isDuplicate = values.some(
    (value, index) =>
      index !== editIndex && toComparableValue(value) === candidate,
  );

  return isDuplicate ? PROFILE_LIST_VALUE_ERROR.DUPLICATE : null;
};

export const upsertAtIndex = <TValue>({
  values,
  value,
  editIndex,
}: {
  values: TValue[];
  value: TValue;
  editIndex: number | null;
}): TValue[] =>
  editIndex === null
    ? [...values, value]
    : values.map((current, index) => (index === editIndex ? value : current));

export const removeAtIndex = <TValue>({
  values,
  index,
}: {
  values: TValue[];
  index: number;
}): TValue[] => values.filter((_, valueIndex) => valueIndex !== index);

export const toEntityFormValues = (
  entity: BusinessProfileEntity | undefined,
): BusinessProfileEntityFormValues =>
  entity
    ? {
        name: entity.name,
        description: entity.description ?? '',
        location: entity.location ?? '',
      }
    : EMPTY_BUSINESS_PROFILE_ENTITY_FORM;

export const toBusinessProfileEntity = (
  values: BusinessProfileEntityFormValues,
): BusinessProfileEntity => ({
  name: values.name.trim(),
  description: normalizeOptionalText(values.description),
  location: normalizeOptionalText(values.location),
});

export const isBusinessProfileCompanySize = (
  value: string,
): value is BusinessProfileCompanySize =>
  BUSINESS_PROFILE_COMPANY_SIZES.some((companySize) => companySize === value);

export const toTextFieldUpsert = ({
  field,
  draft,
}: {
  field: BusinessProfileTextField;
  draft: string;
}): UpsertSurfaceProfileDto =>
  field === BUSINESS_PROFILE_TEXT_FIELD.DESCRIPTION
    ? { description: normalizeOptionalText(draft) }
    : { primaryIndustry: normalizeOptionalText(draft) };

export const getTextFieldValue = ({
  profile,
  field,
}: {
  profile: SurfaceProfileDto | null | undefined;
  field: BusinessProfileTextField;
}): string | null => profile?.[field] ?? null;
